import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle } from 'lucide-react'
import { useSecurityStats } from '../hooks/useCrawls'

function AdoptionRow({
  label,
  count,
  total,
  variant = 'positive',
}: {
  label: string
  count: number
  total: number
  variant?: 'positive' | 'negative'
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  const isGood = variant === 'positive' ? pct >= 80 : pct === 0
  const barColor = variant === 'positive'
    ? (pct >= 80 ? 'bg-green-500/80' : pct >= 50 ? 'bg-yellow-500/80' : 'bg-red-500/80')
    : (pct > 0 ? 'bg-yellow-500/80' : 'bg-green-500/80')

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <div className="flex-1 h-5 rounded-sm bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${count} of ${total}`}>
        {pct > 0 && (
          <div
            className={`h-full rounded-sm transition-all ${barColor}`}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        )}
      </div>
      <span className={`w-16 text-right tabular-nums font-medium ${isGood ? 'text-green-600 dark:text-green-400' : ''}`}>
        {count}/{total}
      </span>
      <span className="w-12 text-right tabular-nums text-muted-foreground">{pct}%</span>
    </div>
  )
}

export function SecurityTab({ crawlId }: { crawlId: string }) {
  const { data, isLoading } = useSecurityStats(crawlId, true)

  if (isLoading || !data) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton className="h-52" />
        <Skeleton className="h-52" />
      </div>
    )
  }

  const { headerStats, serverDistribution } = data
  const total = headerStats.total_200

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Header Adoption</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <AdoptionRow label="HSTS" count={headerStats.has_hsts} total={total} />
          <AdoptionRow label="CSP" count={headerStats.has_csp} total={total} />
          <AdoptionRow label="X-Frame-Options" count={headerStats.has_xfo} total={total} />
          <AdoptionRow label="X-Robots-Tag" count={headerStats.has_x_robots_tag} total={total} />
          <AdoptionRow label="X-Powered-By" count={headerStats.has_x_powered_by} total={total} variant="negative" />
        </CardContent>
      </Card>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Server Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {serverDistribution.length === 0 ? (
              <p className="text-sm text-muted-foreground">No server headers found</p>
            ) : (
              <div className="space-y-2">
                {serverDistribution.map((entry) => (
                  <div key={entry.server} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-muted-foreground truncate mr-2" title={entry.server}>
                      {entry.server}
                    </span>
                    <Badge variant="secondary" className="tabular-nums shrink-0">
                      {entry.count}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {headerStats.has_x_powered_by > 0 && (
          <Card className="border-yellow-500/50">
            <CardContent className="py-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">Technology Stack Exposed</p>
                <p className="text-muted-foreground">
                  {headerStats.has_x_powered_by} page{headerStats.has_x_powered_by !== 1 ? 's' : ''} expose
                  the X-Powered-By header, revealing the server technology. Consider removing this header to
                  reduce the attack surface.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
